import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

const BOT_LABELS = {
  googlebot: 'Google',
  gptbot: 'ChatGPT (GPTBot)',
  claudebot: 'Claude (ClaudeBot)',
  perplexitybot: 'Perplexity',
  browser: 'Browser',
};

function Row({ ok, label, detail }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b last:border-0">
      {ok ? (
        <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
      ) : (
        <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
      )}
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        {detail && <p className="text-xs text-muted-foreground">{detail}</p>}
      </div>
    </div>
  );
}

export default function AuditReportView({ audit }) {
  if (!audit) return null;
  const c = audit.crawlability || {};
  const bots = c.bot_access || [];
  const pages = audit.pages || [];

  return (
    <div className="space-y-6" data-testid="audit-report">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Vindbaarheids-scan</h3>
          <p className="text-sm text-muted-foreground">{audit.domain}</p>
        </div>
        {audit.derived_archetype && (
          <Badge variant="secondary" className="capitalize">
            Archetype: {audit.derived_archetype === 'traffic' ? 'Traffic (schalen)' : 'Zero (vanaf nul)'}
          </Badge>
        )}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Crawlbaarheid</CardTitle>
            <CardDescription>Kan de wereld je site lezen?</CardDescription>
          </CardHeader>
          <CardContent>
            <Row ok={c.robots_txt_found && !c.robots_disallows_all}
                 label="robots.txt"
                 detail={!c.robots_txt_found ? 'Niet gevonden' : c.robots_disallows_all ? 'Blokkeert alles!' : 'Aanwezig, staat crawlen toe'} />
            <Row ok={c.sitemap_found}
                 label="Sitemap"
                 detail={c.sitemap_found ? `${c.sitemap_url_count} URL's` : 'Niet gevonden'} />
            <Row ok={c.llms_txt_found}
                 label="llms.txt"
                 detail={c.llms_txt_found ? 'Aanwezig — AI-crawlers krijgen context' : 'Ontbreekt — kans voor LLM-vindbaarheid'} />
            <Row ok={c.is_prerendered}
                 label="Prerendering"
                 detail={c.is_prerendered ? 'Inhoud staat in de HTML' : 'Lijkt een JS-shell — crawlers zien mogelijk weinig'} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Bot-toegang</CardTitle>
            <CardDescription>Wie mag er binnen?</CardDescription>
          </CardHeader>
          <CardContent>
            {bots.length === 0 && <p className="text-sm text-muted-foreground">Geen data</p>}
            {bots.map((b) => (
              <Row key={b.user_agent}
                   ok={!b.blocked}
                   label={BOT_LABELS[b.user_agent] || b.user_agent}
                   detail={b.blocked ? `Geblokkeerd (HTTP ${b.status_code})` : `Toegang OK (HTTP ${b.status_code})`} />
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pagina's ({pages.length})</CardTitle>
          <CardDescription>Titels, meta-beschrijvingen en structuur</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b">
                <th className="py-2 pr-4 font-medium">URL</th>
                <th className="py-2 pr-4 font-medium">Title</th>
                <th className="py-2 pr-4 font-medium">Meta</th>
                <th className="py-2 pr-4 font-medium">H1</th>
                <th className="py-2 font-medium">Woorden</th>
              </tr>
            </thead>
            <tbody>
              {pages.slice(0, 25).map((p, i) => {
                const titleWarn = !p.title || p.title_length > 60;
                const metaWarn = !p.meta_description || p.meta_description_length > 155;
                return (
                  <tr key={i} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-4 max-w-[220px] truncate text-foreground" title={p.url}>{p.url}</td>
                    <td className="py-2 pr-4 max-w-[200px]">
                      <span className={titleWarn ? 'text-amber-700' : 'text-foreground'}>
                        {titleWarn && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                        {p.title || '—'} ({p.title_length})
                      </span>
                    </td>
                    <td className="py-2 pr-4 max-w-[200px]">
                      <span className={metaWarn ? 'text-amber-700' : 'text-foreground'}>
                        {metaWarn && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                        {p.meta_description ? `${p.meta_description_length} tekens` : 'ontbreekt'}
                      </span>
                    </td>
                    <td className="py-2 pr-4 max-w-[160px] truncate text-foreground">{p.h1 || '—'}</td>
                    <td className="py-2 text-foreground">{p.word_count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
